import { useEffect, useRef, useSyncExternalStore } from 'react';
import { findLayerById } from '../core/layerTree';
import { rgbaToCss } from '../core/color';
import { documentToScreen, type CanvasSize } from '../gl/view';
import { textFontCss, textLayerBounds } from '../engine/textOps';
import { commitTextEdit, updateTextTransient } from '../state/textActions';
import {
  getTextEditSession,
  isTextEditBlurSuppressed,
  subscribeTextEdit,
} from '../state/textEdit';
import { getActiveDocument, useAppStore } from '../state/store';

/**
 * Transparent textarea over the canvas while the Type tool is editing a layer.
 * Canvas2D still paints the glyphs; this field only captures keyboard input.
 */
export function TextEditOverlay({ canvasSize }: { canvasSize: CanvasSize }) {
  const session = useSyncExternalStore(subscribeTextEdit, getTextEditSession, getTextEditSession);
  const doc = useAppStore(getActiveDocument);
  const view = useAppStore((state) =>
    state.activeDocumentId ? (state.views[state.activeDocumentId] ?? null) : null,
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const layer = session && doc ? findLayerById(doc.layers, session.layerId) : null;
  const text = layer?.kind === 'text' ? layer : null;
  const editLayerId = session?.layerId ?? null;

  useEffect(() => {
    if (!editLayerId) return;
    const focus = () => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus({ preventScroll: true });
      const len = node.value.length;
      node.setSelectionRange(len, len);
    };
    // After the placing click's mouseup, focus again on the next frames.
    focus();
    const a = window.requestAnimationFrame(focus);
    const b = window.setTimeout(focus, 0);
    const c = window.setTimeout(focus, 50);
    return () => {
      window.cancelAnimationFrame(a);
      window.clearTimeout(b);
      window.clearTimeout(c);
    };
  }, [editLayerId]);

  if (!session || !doc || !view || !text || canvasSize.width <= 0) return null;

  const box = textLayerBounds(text);
  const topLeft = documentToScreen({ x: box.x, y: box.y }, view, canvasSize);
  const width = Math.max(120, box.width * view.zoom + 8);
  const height = Math.max(text.fontSize * view.zoom * 1.35, box.height * view.zoom + 4);

  return (
    <textarea
      ref={textareaRef}
      className="text-edit-overlay"
      value={text.content}
      spellCheck={false}
      aria-label="Edit text"
      style={{
        left: topLeft.x,
        top: topLeft.y,
        width,
        minHeight: height,
        font: textFontCss({
          ...text,
          fontSize: text.fontSize * view.zoom,
        }),
        caretColor: rgbaToCss(text.color),
        textAlign: text.align,
      }}
      onChange={(event) => updateTextTransient({ content: event.target.value })}
      onBlur={() => {
        if (isTextEditBlurSuppressed()) {
          // Mouseup after the placing click — keep editing and reclaim focus.
          window.requestAnimationFrame(() => textareaRef.current?.focus({ preventScroll: true }));
          return;
        }
        window.setTimeout(() => {
          if (isTextEditBlurSuppressed()) return;
          if (getTextEditSession()?.layerId === session.layerId) commitTextEdit();
        }, 0);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          commitTextEdit();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          commitTextEdit();
        }
        event.stopPropagation();
      }}
      onPointerDown={(event) => event.stopPropagation()}
    />
  );
}
